import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import clockIcon from "../../assets/imgs/crime/clockwise.png";

import NickCarList from "../../components/NickCarList";
import NickCarCaughtPopup from "../../components/NickCarPopup/NickCarCaughtPopup";
import NickCarFailedPopup from "../../components/NickCarPopup/NickCarFailedPopup";
import NickCarSuccessPopup from "../../components/NickCarPopup/NickCarSuccessPopup";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RankXP } from "../../constants/rank";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  finishNick,
  getJailedUntilAction,
  getNextNickCarAction,
  getUserHelperBotInfo,
  nickCarAction,
} from "../../reducers/game.slice";
import { getMyProfile } from "../../reducers/profile.slice";
import { convertDateTimeSimple } from "../../utils/utils";
import useNickCarSSE from "./hooks/useNickCarSSE";

import useStyles from "./index.styles";
import HelperBotWorkingPopup from "../../components/HelperBotPopup/HelperBotWorkingPopup";

const NickCar = () => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const { account } = useWallet();
  const currentTime = useCurrentTime();

  const { myProfile } = useAppSelector((state) => state.profile);
  const { nextNickTime, jailedUntil, userCarBotInfo } = useAppSelector(
    (state) => state.game
  );
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const [openFailedModal, setOpenFailedModal] = useState(false);
  const [openCaughtModal, setOpenCaughtModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openBotWorkingPopup, setOpenBotWorkingPopup] = useState(false);

  const [carItemId, setCarItemId] = useState(0);
  const [newCarType, setNewCarType] = useState(0);
  const [damagePercent, setDamagePercent] = useState(0);
  const [successXpPercent, setSuccessXpPercent] = useState(0);

  const renderStatus = () => {
    if (currentTime < jailedUntil) {
      return "You are in jail now!";
    }

    if (currentTime > nextNickTime) {
      return "Ready to Nick!";
    }

    const timeRemaining = convertDateTimeSimple(nextNickTime - currentTime);
    return `Next crime in: ${timeRemaining}`;
  };

  const handleAttempt = (crimeType: number) => {
    dispatch(nickCarAction({ account, crimeType, signature, signMsg }));
  };

  const onSuccess = useCallback(
    (
      itemId: number,
      carType: number,
      damagePercent: number,
      xpAmount: number
    ) => {
      setOpenSuccessModal(true);
      setNewCarType(carType);
      setDamagePercent(damagePercent);
      setCarItemId(itemId);

      updateBalance(account, signMsg, signature);
      dispatch(getNextNickCarAction(account));
      dispatch(finishNick());

      if (myProfile.rankNumber && myProfile.rankNumber < RankXP.length) {
        const xpPercent =
          (xpAmount /
            (RankXP[myProfile.rankNumber] - RankXP[myProfile.rankNumber - 1])) *
          100;

        setSuccessXpPercent(xpPercent);
      }
      myProfile.name && dispatch(getMyProfile(myProfile.name));
    },
    [account, dispatch, myProfile, signMsg, signature, updateBalance]
  );

  const onCaught = useCallback(() => {
    setOpenCaughtModal(true);
    dispatch(getNextNickCarAction(account));
    dispatch(finishNick());
  }, [account, dispatch]);

  const onFailed = useCallback(() => {
    setOpenFailedModal(true);
    dispatch(getNextNickCarAction(account));
    dispatch(finishNick());
  }, [account, dispatch]);

  useNickCarSSE(account, onSuccess, onCaught, onFailed, () => {});

  useEffect(() => {
    dispatch(getNextNickCarAction(account));
    dispatch(getJailedUntilAction(account));
    dispatch(getUserHelperBotInfo(account));
  }, [account, dispatch]);

  useEffect(() => {
    if (userCarBotInfo.isRunning) {
      // setOpenBotWorkingPopup(true);
    }
  }, [userCarBotInfo]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography
            fontFamily={"Philosopher"}
            color={"white"}
            textAlign={"center"}
            fontSize={36}
            fontWeight={700}
          >
            Nick a car
          </Typography>

          <Button
            startIcon={<Box component="img" src={clockIcon} />}
            variant="contained"
            color="primary"
            size="large"
            className={classes.headerButton}
          >
            {renderStatus()}
          </Button>
        </Box>

        <NickCarList
          handleAttempt={handleAttempt}
          disabled={currentTime < nextNickTime || currentTime < jailedUntil}
        />
      </Box>

      <NickCarFailedPopup
        openModal={openFailedModal}
        setOpenModal={setOpenFailedModal}
      />

      <NickCarCaughtPopup
        openModal={openCaughtModal}
        setOpenModal={setOpenCaughtModal}
      />

      <NickCarSuccessPopup
        carItemId={carItemId}
        newCarType={newCarType}
        damagePercent={damagePercent}
        successXpPercent={successXpPercent}
        openModal={openSuccessModal}
        setOpenModal={setOpenSuccessModal}
      />

      <HelperBotWorkingPopup
        openModal={openBotWorkingPopup}
        setOpenModal={setOpenBotWorkingPopup}
      />
    </Box>
  );
};

export default NickCar;
